import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Badge,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { MdCloudUpload, MdClose, MdWarning, MdCheckCircle } from 'react-icons/md';

const UploadArea = observer(({ store }) => {
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const bgBase = useColorModeValue('gray.50', 'gray.800');
  const textMuted = useColorModeValue('gray.500', 'gray.400');

  const onDrop = useCallback(
    async (accepted) => {
      if (accepted.length === 0) return;
      await store.setSourceFile(accepted[0]);
    },
    [store]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/png': [], 'image/jpeg': [], 'image/webp': [] },
    maxFiles: 1,
    multiple: false,
  });

  if (store.sourcePreview) {
    return (
      <Box>
        <Flex align="center" gap={4} p={4} borderWidth="1px" borderRadius="xl" borderColor={borderColor} bg={bgBase}>
          {/* Preview thumbnail */}
          <Box
            w={20}
            h={20}
            borderRadius="xl"
            overflow="hidden"
            flexShrink={0}
            borderWidth="1px"
            borderColor={borderColor}
            bg="white"
          >
            <img
              src={store.sourcePreview}
              alt="Source"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>

          <Box flex={1} minW={0}>
            <Text fontWeight="semibold" fontSize="sm" isTruncated>
              {store.sourceFile?.name}
            </Text>
            <Text fontSize="xs" color={textMuted} mt={0.5}>
              {store.sourceDimensions.width > 0
                ? `${store.sourceDimensions.width} × ${store.sourceDimensions.height} px`
                : 'Loading…'}
            </Text>
            {store.sourceWarning ? (
              <Flex align="center" gap={1} mt={1}>
                <Icon as={MdWarning} color="orange.400" boxSize={3.5} />
                <Text fontSize="xs" color="orange.500">{store.sourceWarning}</Text>
              </Flex>
            ) : (
              store.sourceDimensions.width > 0 && (
                <Flex align="center" gap={1} mt={1}>
                  <Icon as={MdCheckCircle} color="green.400" boxSize={3.5} />
                  <Text fontSize="xs" color="green.500">Ready to generate</Text>
                </Flex>
              )
            )}
          </Box>

          <Box flexShrink={0}>
            <IconButton
              aria-label="Remove image"
              icon={<MdClose />}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              onClick={() => store.clearSource()}
            />
          </Box>
        </Flex>

        {/* Re-upload hint */}
        <Box
          {...getRootProps()}
          mt={2}
          p={2}
          borderRadius="lg"
          border="1px dashed"
          borderColor={borderColor}
          cursor="pointer"
          textAlign="center"
          _hover={{ borderColor: 'blue.400', bg: activeBg }}
          transition="all 0.15s"
        >
          <input {...getInputProps()} />
          <Text fontSize="xs" color={textMuted}>
            Drop a different image to replace
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      p={10}
      border="2px dashed"
      borderColor={isDragActive ? 'blue.400' : borderColor}
      borderRadius="2xl"
      bg={isDragActive ? activeBg : bgBase}
      cursor="pointer"
      textAlign="center"
      transition="all 0.2s"
      _hover={{ borderColor: 'blue.400', bg: activeBg }}
    >
      <input {...getInputProps()} />
      <Flex direction="column" align="center" gap={3}>
        <Icon
          as={MdCloudUpload}
          boxSize={12}
          color={isDragActive ? 'blue.400' : 'gray.400'}
          transition="color 0.2s"
        />
        <Box>
          <Text fontWeight="semibold" fontSize="md">
            {isDragActive ? 'Drop your image here' : 'Drag & drop your app icon'}
          </Text>
          <Text fontSize="sm" color={textMuted} mt={1}>
            or click to browse — PNG, JPG, WEBP
          </Text>
        </Box>
        <Flex gap={2} flexWrap="wrap" justify="center">
          <Badge colorScheme="green" variant="subtle" fontSize="xs">Recommended: 1024 × 1024</Badge>
          <Badge colorScheme="blue" variant="subtle" fontSize="xs">100% local — never uploaded</Badge>
        </Flex>
      </Flex>
    </Box>
  );
});

export default UploadArea;
